import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import useDarkMode from '../hooks/useDarkMode';
import { CONTACT_MAILTO } from './ui';

/**
 * Navigation, version 2 (September 2026).
 * One sticky header, four items and one button. No dropdowns, no hover
 * menus. Emerald underline on the current page. Under 700px the items
 * move into a drawer that closes on selection or Escape.
 */

const NAV_LINKS = [
  { to: '/why-it-matters', label: 'Why it matters' },
  { to: '/what-we-do',     label: 'What we do' },
  { to: '/what-we-think',  label: 'What we think' },
  { to: '/who-we-are',     label: 'Who we are' },
];

const ThemeToggle = ({ isDark, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
    className="inline-flex items-center justify-center h-11 w-11 rounded-sm text-navy-500 hover:text-navy-800 hover:bg-navy-100 dark:text-navy-400 dark:hover:text-white dark:hover:bg-navy-850 transition-colors"
  >
    {isDark ? (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M12 3v1.5M12 19.5V21M4.22 4.22l1.06 1.06M18.72 18.72l1.06 1.06M3 12h1.5M19.5 12H21M4.22 19.78l1.06-1.06M18.72 5.28l1.06-1.06M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
      </svg>
    ) : (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M21.75 15.5A9.72 9.72 0 0112 21.75 9.75 9.75 0 018.5 2.92a.75.75 0 01.97.97A8.25 8.25 0 0020.86 14.53a.75.75 0 01.89.97z" />
      </svg>
    )}
  </button>
);

const Wordmark = () => (
  <Link to="/" className="flex items-center gap-3 min-h-tap" aria-label="Right Brain Labs, home">
    <img src="/rbl-mark.svg" alt="" width="36" height="36" className="h-9 w-9" />
    <span className="font-sans font-semibold text-[17px] tracking-[-0.01em] text-navy-800 dark:text-white">Right Brain Labs</span>
  </Link>
);

const linkClass = ({ isActive }) =>
  [
    'font-sans text-small font-medium min-h-tap inline-flex items-center border-b-2 transition-colors',
    isActive
      ? 'text-navy-800 border-emerald-500 dark:text-white'
      : 'text-navy-600 border-transparent hover:text-navy-800 dark:text-navy-400 dark:hover:text-white',
  ].join(' ');

const Navigation = () => {
  const { isDark, toggleTheme } = useDarkMode();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const drawerRef = useRef(null);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open]);

  return (
    <header
      className={[
        'sticky top-0 z-50 bg-navy-50 dark:bg-navy-900 border-b transition-colors',
        scrolled ? 'border-navy-200 dark:border-navy-700' : 'border-transparent',
      ].join(' ')}
    >
      <div className="mx-auto max-w-page px-5 md:px-8 h-16 flex items-center justify-between gap-4">
        <Wordmark />

        <nav className="hidden md:flex items-center gap-7" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass}>{l.label}</NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
          <a href={CONTACT_MAILTO}
            className="inline-flex items-center justify-center min-h-tap px-5 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-sans text-button font-semibold transition-colors whitespace-nowrap">
            Start a conversation
          </a>
        </div>

        <div className="flex md:hidden items-center gap-1">
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls="mobile-drawer"
            aria-label={open ? 'Close menu' : 'Open menu'}
            className="inline-flex items-center justify-center h-11 w-11 rounded-sm text-navy-800 dark:text-white hover:bg-navy-100 dark:hover:bg-navy-850"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
              {open
                ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div id="mobile-drawer" ref={drawerRef}
          className="md:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-navy-50 dark:bg-navy-900 border-t border-navy-200 dark:border-navy-700 overflow-y-auto">
          <nav className="px-5 py-4 flex flex-col" aria-label="Primary, mobile">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to}
                className={({ isActive }) => [
                  'font-sans text-[19px] font-medium min-h-[52px] flex items-center border-b border-navy-200 dark:border-navy-700',
                  isActive ? 'text-emerald-600 dark:text-emerald-300' : 'text-navy-800 dark:text-white',
                ].join(' ')}>
                {l.label}
              </NavLink>
            ))}
            <a href={CONTACT_MAILTO}
              className="mt-6 inline-flex items-center justify-center min-h-[48px] rounded-sm bg-emerald-500 text-white font-sans text-button font-semibold">
              Start a conversation
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navigation;
